import type { TrackedInsertion } from '../../api/word/word-service'
import {
  insertHtml,
  replaceSelectedTable,
  replaceSelectionWithHtml,
} from '../../api/word/word-service'
import { TELEMETRY_ENABLED } from '../../constants/telemetry'
import type { TelemetryTask } from '../../types/telemetry'
import { sanitizeForInsert } from '../html-sanitization'
import { reportInsertion } from '../insertion-telemetry'

export type InsertSanitizedHtmlOptions = {
  location?: Word.InsertLocation
  /** Word native Track Changes (agent inserts only). Default false. */
  trackChanges?: boolean
  /**
   * When set and telemetry is enabled, tracks the inserted range for retention
   * reporting (UI bubble Insert). Not used by agent skill handlers.
   */
  telemetryTask?: TelemetryTask
}

/** True when html contains a complete `<table>…</table>` block (not prose mentioning "table"). */
function hasTableElement(html: string): boolean {
  return /<table[\s>][\s\S]*?<\/table>/i.test(html)
}

/**
 * Table-only LLM output may use replaceSelectedTable; mixed prose + table uses
 * replaceSelectionWithHtml. This is a lightweight check rather than an HTML parser,
 * so unusual markup may still be treated as mixed content; that safe fallback is acceptable.
 */
function isTableOnlyInsertHtml(html: string): boolean {
  if (!hasTableElement(html)) return false

  const withoutTables = html.replace(/<table[\s\S]*?<\/table>/gi, '')
  const textOutsideTables = withoutTables
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|\u00A0/g, '')
    .replace(/\u200B/g, '')
    .trim()

  return textOutsideTables.length === 0
}

/**
 * Sanitize and insert HTML at the current Word selection, routing table-only / mixed /
 * prose the same way as the chat Insert action.
 */
export async function insertSanitizedHtml(
  html: string,
  options: InsertSanitizedHtmlOptions = {}
): Promise<TrackedInsertion | null> {
  const location = options.location ?? Word.InsertLocation.replace
  const telemetryTask = TELEMETRY_ENABLED ? options.telemetryTask : undefined
  const wordOptions = {
    trackChanges: options.trackChanges ?? false,
    trackForRetention: Boolean(telemetryTask),
  }

  const sanitized = sanitizeForInsert(html)
  let tracked: TrackedInsertion | null

  const isReplaceLocation = location === Word.InsertLocation.replace

  if (isReplaceLocation && isTableOnlyInsertHtml(html)) {
    // replace the current selection with a table only
    const tableResult = await replaceSelectedTable(sanitized, wordOptions)
    tracked = tableResult.replaced
      ? tableResult.tracked
      : await insertHtml(sanitized, { ...wordOptions, location })
  } else if (isReplaceLocation && hasTableElement(html)) {
    // replace the current selection with a table and prose (addresses buggy Word behavior)
    tracked = await replaceSelectionWithHtml(sanitized, wordOptions)
  } else {
    // insert at the current selection
    tracked = await insertHtml(sanitized, { ...wordOptions, location })
  }

  if (telemetryTask && tracked) {
    reportInsertion(telemetryTask, tracked)
  }

  return tracked
}
